import { db } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';

// Sample products data
const sampleProducts = [
  {
    name: "Classic Burger",
    description: "Juicy beef burger with lettuce, tomato, and special sauce",
    price: 350
  },
  {
    name: "Chicken Pizza",
    description: "Large pizza with chicken, mushrooms, and mozzarella cheese",
    price: 850
  },
  {
    name: "Pasta Carbonara",
    description: "Creamy pasta with bacon and parmesan cheese",
    price: 450
  },
  {
    name: "Caesar Salad",
    description: "Fresh romaine lettuce with caesar dressing and croutons",
    price: 280
  },
  {
    name: "Fish and Chips",
    description: "Crispy battered fish with french fries",
    price: 550
  },
  {
    name: "Grilled Chicken",
    description: "Marinated grilled chicken with vegetables",
    price: 480
  },
  {
    name: "Vegetable Sandwich",
    description: "Fresh vegetables with cheese on whole wheat bread",
    price: 250
  },
  {
    name: "Chocolate Milkshake",
    description: "Rich and creamy chocolate milkshake",
    price: 180
  },
  {
    name: "French Fries",
    description: "Crispy golden french fries with ketchup",
    price: 150
  },
  {
    name: "Soft Drink",
    description: "Choice of cola, sprite, or fanta",
    price: 80
  },
  {
    name: "Beef Steak",
    description: "Premium beef steak with mushroom sauce",
    price: 950
  },
  {
    name: "Iced Coffee",
    description: "Cold brew coffee with ice and cream",
    price: 200
  }
];

// Function to add sample products
export const addSampleProducts = async () => {
  try {
    console.log('Adding sample products...');
    const productsCollection = collection(db, 'products');

    let successCount = 0;
    for (const product of sampleProducts) {
      await addDoc(productsCollection, product);
      successCount++;
      console.log(`Added: ${product.name}`);
    }

    console.log(`✅ Successfully added ${successCount} products!`);
    return { success: true, count: successCount };
  } catch (error) {
    console.error('Error adding sample products:', error);
    return { success: false, error: error.message };
  }
};

// Function to add sample orders
export const addSampleOrders = async (count = 3) => {
  try {
    console.log('Adding sample orders...');

    const order1 = {
      items: [
        { id: 'sample1', name: 'Classic Burger', price: 350, quantity: 2 },
        { id: 'sample2', name: 'French Fries', price: 150, quantity: 1 },
        { id: 'sample3', name: 'Soft Drink', price: 80, quantity: 2 }
      ],
      totalAmount: 930,
      status: 'pending',
      timestamp: new Date().toISOString()
    };

    const order2 = {
      items: [
        { id: 'sample4', name: 'Chicken Pizza', price: 850, quantity: 1 },
        { id: 'sample5', name: 'Iced Coffee', price: 200, quantity: 2 }
      ],
      totalAmount: 1250,
      status: 'pending',
      timestamp: new Date().toISOString()
    };

    const order3 = {
      items: [
        { id: 'sample6', name: 'Pasta Carbonara', price: 450, quantity: 1 },
        { id: 'sample7', name: 'Caesar Salad', price: 280, quantity: 1 },
        { id: 'sample8', name: 'Chocolate Milkshake', price: 180, quantity: 1 }
      ],
      totalAmount: 910,
      status: 'pending',
      timestamp: new Date().toISOString()
    };

    const ordersCollection = collection(db, 'orders');

    await addDoc(ordersCollection, order1);
    console.log('Added Order 1');

    await addDoc(ordersCollection, order2);
    console.log('Added Order 2');

    await addDoc(ordersCollection, order3);
    console.log('Added Order 3');

    console.log(`✅ Successfully added ${count} sample orders!`);
    return { success: true, count: count };
  } catch (error) {
    console.error('Error adding sample orders:', error);
    return { success: false, error: error.message };
  }
};

// Function to add sample sales (for reports testing)
export const addSampleSales = async () => {
  try {
    console.log('Adding sample sales data...');
    const salesCollection = collection(db, 'sales');

    // Create sales for different dates
    const today = new Date();
    const dates = [
      new Date(today), // Today
      new Date(today.setDate(today.getDate() - 1)), // Yesterday
      new Date(today.setDate(today.getDate() - 1)), // 2 days ago
      new Date(today.setDate(today.getDate() - 3)), // 5 days ago
      new Date(today.setDate(today.getDate() - 25)) // Last month
    ];

    const sampleSales = [
      {
        orderId: 'sample-order-1',
        items: [
          { name: 'Classic Burger', quantity: 2, price: 350 },
          { name: 'French Fries', quantity: 1, price: 150 }
        ],
        subtotal: 850,
        gst: 153,
        totalWithGST: 1003,
        date: dates[0].toISOString()
      },
      {
        orderId: 'sample-order-2',
        items: [
          { name: 'Chicken Pizza', quantity: 1, price: 850 }
        ],
        subtotal: 850,
        gst: 153,
        totalWithGST: 1003,
        date: dates[0].toISOString()
      },
      {
        orderId: 'sample-order-3',
        items: [
          { name: 'Pasta Carbonara', quantity: 2, price: 450 },
          { name: 'Soft Drink', quantity: 2, price: 80 }
        ],
        subtotal: 1060,
        gst: 190.8,
        totalWithGST: 1250.8,
        date: dates[1].toISOString()
      },
      {
        orderId: 'sample-order-4',
        items: [
          { name: 'Beef Steak', quantity: 1, price: 950 },
          { name: 'Caesar Salad', quantity: 1, price: 280 }
        ],
        subtotal: 1230,
        gst: 221.4,
        totalWithGST: 1451.4,
        date: dates[2].toISOString()
      },
      {
        orderId: 'sample-order-5',
        items: [
          { name: 'Fish and Chips', quantity: 2, price: 550 }
        ],
        subtotal: 1100,
        gst: 198,
        totalWithGST: 1298,
        date: dates[3].toISOString()
      }
    ];

    let successCount = 0;
    for (const sale of sampleSales) {
      await addDoc(salesCollection, sale);
      successCount++;
      console.log(`Added sale for ${new Date(sale.date).toLocaleDateString()}`);
    }

    console.log(`✅ Successfully added ${successCount} sample sales!`);
    return { success: true, count: successCount };
  } catch (error) {
    console.error('Error adding sample sales:', error);
    return { success: false, error: error.message };
  }
};
